import { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log to error reporting service in production
        if (process.env.NODE_ENV === 'production') {
            // Could send to error tracking service like Sentry
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen bg-gradient-to-b from-cosmic-900 via-cosmic to-cosmic-900 flex items-center justify-center px-4">
                    <div className="max-w-md w-full text-center">
                        <div className="text-8xl mb-6">🙏</div>
                        <h1 className="text-3xl font-poppins font-bold text-divine mb-4">
                            कुछ गलत हो गया
                        </h1>
                        <h2 className="text-xl font-poppins text-offwhite mb-6">
                            Something Went Wrong
                        </h2>
                        <p className="text-divine-200 font-noto-serif mb-8">
                            We apologize for the inconvenience. Please try refreshing the page or return home.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-3 bg-divine hover:bg-divine-light text-cosmic font-poppins font-semibold rounded-lg transition-all duration-300"
                            >
                                Refresh Page
                            </button>
                            <Link
                                to="/"
                                className="px-6 py-3 bg-cosmic-light/30 hover:bg-cosmic-light/50 text-divine border-2 border-divine/40 hover:border-divine font-poppins font-semibold rounded-lg transition-all duration-300"
                            >
                                Return Home
                            </Link>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}